import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { Logo } from './components/Logo'
import { Markdown } from '@/shared/components/markdown/Markdown'
import { Footer } from './components/Footer'
import {
  DEFAULT_LANDING_CONTENT,
  useLandingContent,
  useLandingContentStore,
  type LegalDocKey,
} from '@/features/admin/content/landingContent'

const LEGAL_TITLES: Record<LegalDocKey, string> = {
  terms: 'Términos y condiciones',
  privacy: 'Política de privacidad',
  accountDeletion: 'Dar de baja tu cuenta',
  aiConnector: 'Conecta tu IA a Tecopos',
  aiConnectorPrivacy: 'Privacidad del conector de IA',
}

/** Página pública de una página central (Términos / Privacidad / Eliminar cuenta / Conector de IA). Contenido en Markdown. */
export function LegalPage({ doc }: { doc: LegalDocKey }) {
  const content = useLandingContent()
  const status = useLandingContentStore((s) => s.status)
  // Si el servidor del portal no responde, la página legal enseña su semilla en
  // vez de un error: Google Play rechaza las apps cuya política no se puede
  // leer, y la semilla es el texto publicado mientras nadie lo edite en el
  // panel (Adrian, 2026-09-28). El resto del sitio sigue sin pintar semillas.
  const md = (status === 'error' ? DEFAULT_LANDING_CONTENT[doc] : content[doc] ?? '').trim()

  // Sin esto la pestaña dice «TecoPOS — Portal» en las tres páginas legales.
  useEffect(() => {
    const previous = document.title
    document.title = `${LEGAL_TITLES[doc]} — TecoPOS`
    return () => {
      document.title = previous
    }
  }, [doc])

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <header className="sticky top-0 z-30 border-b border-black/5 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4 lg:px-6">
          <Link to="/" className="inline-flex items-center">
            <Logo />
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Volver al inicio
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 px-5 py-12 lg:px-6 lg:py-16">
        {status === 'loading' ? (
          <div role="status" aria-busy="true" className="space-y-3">
            <span className="sr-only">Cargando el contenido…</span>
            <div className="h-8 w-2/3 animate-pulse rounded bg-neutral-100" />
            <div className="h-4 w-full animate-pulse rounded bg-neutral-100" />
            <div className="h-4 w-5/6 animate-pulse rounded bg-neutral-100" />
          </div>
        ) : md ? (
          <Markdown>{md}</Markdown>
        ) : (
          <div>
            <h1 className="font-serif text-3xl">{LEGAL_TITLES[doc]}</h1>
            <p className="mt-4 text-muted-foreground">Esta página aún no tiene contenido.</p>
          </div>
        )}
      </main>

      <Footer />
    </div>
  )
}
